import { useState, useEffect } from 'react';
import { getPhoneErrors, getPhoneErrorStatistics } from '@services/api';
import './ErrorsTab.css';

export default function ErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [twilioAccountSid, setTwilioAccountSid] = useState(null);

  // Get the Twilio Account SID from auth token for console links
  useEffect(() => {
    try {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        const payload = authToken.split('.')[1];
        if (payload) {
          const decoded = JSON.parse(atob(payload));
          if (decoded.twilio_account_sid) {
            setTwilioAccountSid(decoded.twilio_account_sid);
          }
        }
      }
    } catch (err) {
      console.debug('Could not extract account SID from token:', err);
    }
  }, []);

  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    endDate: new Date(),
    category: '',
    severity: '',
    callSid: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  // Fetch errors and statistics in a single API call
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPhoneErrors({
        ...filters,
        page,
        pageSize,
        includeStatistics: true, // Request statistics in the same call
      });
      setErrors(response.errors || []);
      setTotalPages(response.totalPages || 0);
      setTotalCount(response.totalCount || 0);

      // Set statistics if included in response
      if (response.statistics) {
        setStatistics(response.statistics);
      }
    } catch (err) {
      setError('Failed to fetch errors: ' + (err.message || 'Unknown error'));
      console.error('Error fetching phone errors:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [filters, page, pageSize]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
      endDate: new Date(),
      category: '',
      severity: '',
      callSid: '',
    });
    setPage(1);
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return '#dc3545';
      case 'high':
        return '#fd7e14';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  const errorCategories = [
    'Outgoing',
    'Incoming',
    'IVR',
    'AIAgent',
    'Transfer',
    'Voicemail',
    'Recording',
    'Token',
    'Webhook',
  ];

  const severityLevels = ['Low', 'Medium', 'High', 'Critical'];

  const getTwilioConsoleUrl = (callSid) => {
    if (!callSid) return null;

    // Validate callSid format to prevent open-redirect / DOM XSS via URL injection.
    // Twilio call SIDs always start with "CA" followed by 32 hex characters.
    if (!/^CA[0-9a-fA-F]{32}$/.test(callSid)) return null;

    const frameUrl = `/console/voice/calls/logs/${callSid}`;
    const base = new URL('https://console.twilio.com/us1/monitor/logs/calls');
    base.searchParams.set('frameUrl', frameUrl);
    base.searchParams.set('x-target-region', 'us1');
    base.searchParams.set('__override_layout__', 'embed');
    base.searchParams.set('bifrost', 'true');

    if (twilioAccountSid) {
      base.searchParams.set('accountSid', twilioAccountSid);
    }
    return base.toString();
  };

  const CallSidLink = ({ sid }) => {
    if (!sid) return null;
    return (
      <a
        href={getTwilioConsoleUrl(sid)}
        target="_blank"
        rel="noopener noreferrer"
        className="call-sid-link"
        title="View in Twilio Console"
        onClick={(e) => e.stopPropagation()}
      >
        {sid}
      </a>
    );
  };

  return (
    <div className="errors-tab">
      {/* Header */}
      <div className="errors-header">
        <h2>📋 System Errors</h2>
        <button
          className="filter-toggle-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? '⬆ Hide Filters' : '⬇ Show Filters'}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate.toISOString().slice(0, 16)}
              onChange={(e) =>
                handleFilterChange('startDate', new Date(e.target.value))
              }
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate.toISOString().slice(0, 16)}
              onChange={(e) =>
                handleFilterChange('endDate', new Date(e.target.value))
              }
            />
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {errorCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severity Levels</option>
              {severityLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Call SID</label>
            <input
              type="text"
              placeholder="Filter by Twilio Call SID"
              value={filters.callSid}
              onChange={(e) => handleFilterChange('callSid', e.target.value)}
            />
          </div>

          <button className="clear-filters-button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="statistics-panel">
          <div className="stat-card">
            <div className="stat-number">{statistics.totalErrors}</div>
            <div className="stat-label">Total Errors</div>
          </div>

          {statistics.bySeverity && statistics.bySeverity.length > 0 && (
            <>
              {statistics.bySeverity.map((stat) => (
                <div key={stat.severity} className="stat-card">
                  <div
                    className="stat-number"
                    style={{ color: getSeverityColor(stat.severity) }}
                  >
                    {stat.count}
                  </div>
                  <div className="stat-label">{stat.severity}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Error List */}
      <div className="errors-container">
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading errors...</div>
        ) : errors.length === 0 ? (
          <div className="no-errors">No errors found for the selected filters</div>
        ) : (
          <>
            <div className="errors-list">
              {errors.map((err) => (
                <div
                  key={err.phoneErrorId}
                  className="error-item"
                  onClick={() => setSelectedError(selectedError?.phoneErrorId === err.phoneErrorId ? null : err)}
                  style={{
                    borderLeft: `4px solid ${getSeverityColor(err.severity)}`,
                  }}
                >
                  <div className="error-item-header">
                    <div className="error-basic-info">
                      <span
                        className="severity-badge"
                        style={{ backgroundColor: getSeverityColor(err.severity) }}
                      >
                        {err.severity}
                      </span>
                      <span className="error-category">{err.category}</span>
                      <span className="error-service">{err.service}</span>
                    </div>
                    <span className="error-timestamp">
                      {new Date(err.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className="error-message-preview">
                    {err.errorMessage?.substring(0, 100)}
                    {err.errorMessage?.length > 100 ? '...' : ''}
                  </div>

                  {err.callSid && (
                    <div className="error-call-info">
                      <span className="label">Call SID:</span>
                      <CallSidLink sid={err.callSid} />
                    </div>
                  )}

                  {/* Expanded Details */}
                  {selectedError?.phoneErrorId === err.phoneErrorId && (
                    <div className="error-details">
                      <div className="detail-section">
                        <h4>Error Message</h4>
                        <p>{err.errorMessage}</p>
                      </div>

                      {err.callSid && (
                        <div className="detail-section">
                          <h4>Call Information</h4>
                          <p>
                            <strong>Call SID:</strong>{' '}
                            <CallSidLink sid={err.callSid} />
                          </p>
                          {err.parentCallSid && (
                            <p>
                              <strong>Parent Call SID:</strong>{' '}
                              <CallSidLink sid={err.parentCallSid} />
                            </p>
                          )}
                        </div>
                      )}

                      {err.contactId && (
                        <div className="detail-section">
                          <h4>Contact Information</h4>
                          <p>
                            <strong>Contact ID:</strong> {err.contactId}
                          </p>
                        </div>
                      )}

                      {err.additionalContext && (
                        <div className="detail-section">
                          <h4>Additional Context</h4>
                          <pre className="context-json">
                            {typeof err.additionalContext === 'string'
                              ? err.additionalContext
                              : JSON.stringify(err.additionalContext, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Previous
              </button>

              <div className="page-info">
                Page {page} of {totalPages} ({totalCount} total errors)
              </div>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
